import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { FrameworkWizardGroup, RequirementDraft } from '../frameworks.types'

type CustomFrameworkWizardContextType = {
  name: string
  setName: (value: string) => void
  description: string
  setDescription: (value: string) => void
  requirementDrafts: RequirementDraft[]
  setRequirementDrafts: React.Dispatch<React.SetStateAction<RequirementDraft[]>>
  /** Requirement groups for the category select; persisted when the framework exists. */
  groups: FrameworkWizardGroup[]
  setGroups: React.Dispatch<React.SetStateAction<FrameworkWizardGroup[]>>
  /** When set, requirement creation skips Account.CreateCustomFramework and uses this base model id. */
  appendComplianceFrameworkModelId: string | null
  setAppendComplianceFrameworkModelId: (value: string | null) => void
  /** True when the user entered this flow via the Add Compliance Framework picker. */
  isFromAddFrameworkFlow: boolean
  setIsFromAddFrameworkFlow: (value: boolean) => void
  reset: () => void
}

const CustomFrameworkWizardContext =
  createContext<CustomFrameworkWizardContextType>({
    name: '',
    setName: () => undefined,
    description: '',
    setDescription: () => undefined,
    requirementDrafts: [],
    setRequirementDrafts: () => undefined,
    groups: [],
    setGroups: () => undefined,
    appendComplianceFrameworkModelId: null,
    setAppendComplianceFrameworkModelId: () => undefined,
    isFromAddFrameworkFlow: false,
    setIsFromAddFrameworkFlow: () => undefined,
    reset: () => undefined,
  })

export const CustomFrameworkWizardProvider = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [requirementDrafts, setRequirementDrafts] = useState<
    RequirementDraft[]
  >([])
  const [
    appendComplianceFrameworkModelId,
    setAppendComplianceFrameworkModelId,
  ] = useState<string | null>(null)
  const [isFromAddFrameworkFlow, setIsFromAddFrameworkFlow] = useState(false)
  const [groups, setGroups] = useState<FrameworkWizardGroup[]>([])

  const reset = useCallback(() => {
    setName('')
    setDescription('')
    setRequirementDrafts([])
    setAppendComplianceFrameworkModelId(null)
    setIsFromAddFrameworkFlow(false)
    setGroups([])
  }, [])

  const value = useMemo<CustomFrameworkWizardContextType>(
    () => ({
      name,
      setName,
      description,
      setDescription,
      requirementDrafts,
      setRequirementDrafts,
      groups,
      setGroups,
      appendComplianceFrameworkModelId,
      setAppendComplianceFrameworkModelId,
      isFromAddFrameworkFlow,
      setIsFromAddFrameworkFlow,
      reset,
    }),
    [
      name,
      description,
      requirementDrafts,
      groups,
      appendComplianceFrameworkModelId,
      isFromAddFrameworkFlow,
      reset,
    ],
  )

  return (
    <CustomFrameworkWizardContext.Provider value={value}>
      {children}
    </CustomFrameworkWizardContext.Provider>
  )
}

export const useCustomFrameworkWizard = (): CustomFrameworkWizardContextType =>
  useContext(CustomFrameworkWizardContext)
